import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  ArrowSquareOut,
  ArrowLeft,
  Bank,
  CurrencyDollar,
  CheckCircle,
  Warning,
  Lightning,
  Copy as CopyIcon,
} from "@phosphor-icons/react";

// Step content — kept inline (not extracted) because this page exists
// to be read, not iterated on. The operator opens this page once during
// initial setup, follows the steps, then never returns unless something
// breaks. Keeping it as plain JSX makes the prose easy to update.

const STEPS = [
  {
    n: 1,
    title: "Open Wise → grab the USD account details",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          In Wise Business, open the <b>USD balance jar</b> → click{" "}
          <b>“Get account details”</b>. You're looking for the{" "}
          <b>ACH routing number</b> and the <b>account number</b> for
          your Wise-hosted USD account (it's a real US-domiciled account
          number issued by Community Federal Savings Bank, the same one
          Wise uses to receive USD from anywhere).
        </p>
        <p className="text-[#4A5C50] mt-3">
          Copy these to a scratchpad — you'll paste them into Stripe in
          step 2.
        </p>
        <a
          href="https://wise.com/business/account"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Wise Business <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 2,
    title: "Add the Wise account to Stripe → External accounts",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          In the Stripe Dashboard, go to{" "}
          <b>Settings → Payouts → Bank accounts and debit cards</b>.
          Click <b>“+ Add bank account”</b>. Enter:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-[#4A5C50] list-disc list-inside">
          <li>Country: <b>United States</b></li>
          <li>Currency: <b>USD</b></li>
          <li>Routing number: <span className="mono">your Wise ACH routing #</span></li>
          <li>Account number: <span className="mono">your Wise account #</span></li>
          <li>Account holder type: <b>Business</b> (or Individual if you signed up that way)</li>
        </ul>
        <p className="text-[#4A5C50] mt-3">
          Stripe will fingerprint the account and send <b>two micro-deposits</b>{" "}
          (a few cents each) over the next 1-2 business days. You'll need
          to verify them in step 3.
        </p>
        <a
          href="https://dashboard.stripe.com/settings/payouts"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Stripe → Payouts <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
  {
    n: 3,
    title: "Verify the micro-deposits in Stripe",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          When the micro-deposits land in your Wise USD balance,
          they'll appear in the Wise transaction feed labeled{" "}
          <span className="mono">“STRIPE VERIFICATION”</span>. Note both
          amounts (e.g. <span className="mono">$0.32</span> and{" "}
          <span className="mono">$0.45</span>).
        </p>
        <p className="text-[#4A5C50] mt-3">
          Back in Stripe → Payouts, click the unverified account and enter
          the two amounts. Once verified, the account becomes{" "}
          <b>eligible for payouts</b>.
        </p>
      </>
    ),
  },
  {
    n: 4,
    title: "Set Wise as the default payout destination (optional)",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          By default Stripe will continue paying out to your{" "}
          <b>first</b> verified bank. If you want Stripe to auto-route
          payouts to Wise instead — handy because Wise charges
          ~0.4% to convert USD → VND vs traditional correspondent banks
          at 3-5% — open the Wise row in Stripe → Payouts → ⋯ →{" "}
          <b>“Set as default”</b>.
        </p>
        <div className="mt-3 rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/5 p-3 text-sm text-[#8C6B21]">
          <b>Trade-off:</b> If you set Wise as default and Wise's USD
          balance jar isn't created or your KYB lapses, Stripe payouts
          will fail. Most operators keep a primary US bank as default
          and use Wise only for surge sweeps.
        </div>
      </>
    ),
  },
  {
    n: 5,
    title: "Trigger a manual payout when this page emails you",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          When Stripe's available USD balance crosses{" "}
          <b>$1,000</b> (configurable via the{" "}
          <span className="mono">STRIPE_SWEEP_THRESHOLD_USD</span> env
          var), the daily monitor will email{" "}
          <span className="mono">alex.schemmelt@gmail.com</span> (or
          whatever you set in{" "}
          <span className="mono">STRIPE_SWEEP_NOTIFY_EMAIL</span>) with
          a sweep reminder.
        </p>
        <p className="text-[#4A5C50] mt-3">
          When you get the email: open{" "}
          <b>Stripe → Payouts → New payout</b>, choose your Wise USD
          external account, enter the surplus amount, and click{" "}
          <b>Pay out</b>. The funds will land in your Wise USD balance
          within 1-2 business days where they can then fund Vietnamese
          (and 14 other international) Direct Deposit recipients.
        </p>
        <a
          href="https://dashboard.stripe.com/payouts"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Stripe → Payouts <ArrowSquareOut size={14} />
        </a>
      </>
    ),
  },
];

const ENV_VARS = [
  {
    key: "STRIPE_SWEEP_THRESHOLD_USD",
    default: "1000",
    note: "Reminder fires when Stripe's USD available balance crosses this.",
  },
  {
    key: "STRIPE_SWEEP_NOTIFY_EMAIL",
    default: "alex.schemmelt@gmail.com",
    note: "Comma-separated. Who gets the sweep reminder.",
  },
  {
    key: "STRIPE_SWEEP_REMINDER_DAYS",
    default: "7",
    note: "Minimum days between reminders — prevents inbox spam.",
  },
];

export default function StripeToWiseGuide() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const runCheck = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/stripe/check-balance");
      setStatus(r.data);
    } catch {
      setStatus({ ok: false, skipped_reason: "endpoint error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runCheck();
  }, [runCheck]);

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="stripe-to-wise-guide">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin/setup"
          className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]"
          data-testid="stripe-guide-back"
        >
          <ArrowLeft size={14} /> Back to Setup Checklist
        </Link>

        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
            Walkthrough
          </p>
          <h1 className="serif text-4xl mt-1">
            Stripe → Wise sweep
          </h1>
          <p className="text-[#4A5C50] mt-3 leading-relaxed max-w-2xl">
            How to wire your Wise USD jar to Stripe as an external payout
            account, so when Stripe's balance grows above the sweep
            threshold you can manually transfer the surplus to Wise in
            two clicks — funding international payouts at Wise's ~0.4%
            FX rate instead of correspondent-bank rates.
          </p>
        </header>

        {/* Live status tile — same shape as /admin/setup but inlined so
            operators reading the guide can run the check from this page. */}
        <div
          className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
          data-testid="stripe-balance-tile"
        >
          <div className="flex items-start gap-4">
            <span className="shrink-0 w-12 h-12 rounded-2xl bg-[#184A30] text-white flex items-center justify-center">
              <CurrencyDollar size={22} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
                Stripe USD available
              </p>
              {status && status.ok ? (
                <>
                  <p
                    className="serif text-3xl mt-1"
                    data-testid="stripe-balance-amount"
                  >
                    ${(status.available_usd ?? 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-xs text-[#4A5C50] mt-1">
                    Pending: ${(status.pending_usd ?? 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    · Sweep threshold: $
                    {(status.threshold_usd ?? 0).toLocaleString()}
                  </p>
                  {status.above_threshold ? (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#CBA35C]/15 border border-[#CBA35C]/40 px-3 py-1.5 text-[#8C6B21] text-sm">
                      <Lightning size={14} weight="fill" /> Above threshold — time to sweep.
                    </div>
                  ) : (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#184A30]/10 border border-[#184A30]/20 px-3 py-1.5 text-[#184A30] text-sm">
                      <CheckCircle size={14} weight="fill" /> Below threshold — nothing to do.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="serif text-2xl mt-1 text-[#4A5C50]">
                    Unavailable
                  </p>
                  <p className="text-xs text-[#4A5C50] mt-1 flex items-center gap-1">
                    <Warning size={12} weight="fill" className="text-[#B04E2F]" />{" "}
                    {status?.skipped_reason || "Run the check to see the live balance"}
                  </p>
                </>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={runCheck}
              disabled={loading}
              className="rounded-xl border-[#184A30] text-[#184A30] text-xs"
              data-testid="stripe-balance-refresh"
            >
              {loading ? "Checking…" : "Run check now"}
            </Button>
          </div>
        </div>

        {/* The actual steps */}
        <ol className="mt-8 space-y-5" data-testid="stripe-guide-steps">
          {STEPS.map((s) => (
            <li
              key={s.n}
              className="rounded-2xl border border-[#E5E0D8] bg-white p-5"
              data-testid={`stripe-guide-step-${s.n}`}
            >
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-9 h-9 rounded-full bg-[#184A30] text-white flex items-center justify-center text-sm serif">
                  {s.n}
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="serif text-xl">{s.title}</h2>
                  <div className="mt-2 text-sm">{s.body}</div>
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* Env var reference */}
        <section className="mt-8 rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <div className="flex items-center gap-2 text-[#184A30]">
            <Bank size={18} weight="duotone" />
            <h3 className="serif text-lg">Environment variables</h3>
          </div>
          <p className="text-sm text-[#4A5C50] mt-1">
            Change these in the Deploy Panel (NOT in <span className="mono">.env</span>{" "}
            directly — production reads them from the platform).
          </p>
          <div className="mt-4 divide-y divide-[#E5E0D8]">
            {ENV_VARS.map((v) => (
              <div
                key={v.key}
                className="py-3 flex items-start gap-3"
                data-testid={`stripe-guide-env-${v.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-[#FBF9F6] border border-[#E5E0D8] rounded px-2 py-0.5 mono">
                      {v.key}
                    </code>
                    <span className="text-xs text-[#4A5C50]">
                      default <span className="mono">{v.default}</span>
                    </span>
                  </div>
                  <p className="text-xs text-[#4A5C50] mt-1">{v.note}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-[#4A5C50] text-xs"
                  onClick={() => navigator.clipboard?.writeText(v.key)}
                  title="Copy variable name"
                  data-testid={`stripe-guide-copy-${v.key}`}
                >
                  <CopyIcon size={14} />
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* Why not automated */}
        <section className="mt-8 rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <h3 className="serif text-lg">Why isn't this automated?</h3>
          <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
            Stripe has no public API for transferring directly to a
            third-party fintech like Wise. The real-money paths are:
          </p>
          <ul className="text-sm text-[#4A5C50] mt-2 space-y-1 list-disc list-inside">
            <li>
              <b>Stripe Payouts to an external bank</b> (this walkthrough) —
              available out-of-the-box once you verify the Wise USD account.
            </li>
            <li>
              <b>Stripe Treasury OutboundPayments</b> — gated by Stripe
              Sales, requires a separate platform-account onboarding.
              We've scaffolded the v2 Money Management API call in{" "}
              <span className="mono">backend/routes/global_payouts.py</span>{" "}
              behind <span className="mono">GLOBAL_PAYOUTS_ENABLED</span>{" "}
              if you ever get that enabled.
            </li>
          </ul>
          <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
            Until one of those is wired, this monitor keeps a human in
            the loop so a Wise downtime or KYB hiccup can never cause
            stuck funds.
          </p>
        </section>

        <div className="mt-8 text-center">
          <Link
            to="/admin/setup"
            className="text-sm text-[#184A30] hover:underline"
          >
            ← Back to Setup Checklist
          </Link>
        </div>
      </div>
    </div>
  );
}
